/**
 * SmartContent - Intelligent content renderer for OpenAI messages and completions
 * Shared by TraceDetail and SpanTree
 */

import { useState } from 'react';
import { useI18n } from '@/i18n';

// ============================================================================
// Types
// ============================================================================

interface ToolCall {
  id?: string;
  type?: string;
  function?: { name?: string; arguments?: string };
}

interface CompletionMessage {
  content?: string;
  role?: string;
  tool_calls?: ToolCall[];
  reasoning_content?: string;
  provider_specific_fields?: Record<string, unknown>;
}

interface CompletionResponse {
  choices?: Array<{ message?: CompletionMessage; finish_reason?: string }>;
  model?: string;
  usage?: Record<string, unknown>;
}

// ============================================================================
// Format Detection
// ============================================================================

/** Detect OpenAI messages array: [{role, content}, ...] */
function isMessagesArray(data: unknown): data is Array<{ role: string; content: unknown; tool_calls?: ToolCall[]; reasoning_content?: string }> {
  return Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'role' in data[0];
}

/** Detect OpenAI completion response: {choices: [{message: {content}}], ...} */
function isCompletionResponse(data: unknown): data is CompletionResponse {
  return typeof data === 'object' && data !== null && 'choices' in data && Array.isArray((data as Record<string, unknown>).choices);
}

/** Extract text content from a message's content field (string or content array) */
function extractTextContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((part: unknown) => {
        if (typeof part === 'string') return part;
        if (typeof part === 'object' && part !== null) {
          const p = part as Record<string, unknown>;
          if (p.type === 'text' && typeof p.text === 'string') return p.text;
          if (p.type === 'image_url') return '[Image]';
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return typeof content === 'undefined' || content === null ? '' : JSON.stringify(content);
}

// ============================================================================
// Role Styles
// ============================================================================

const ROLE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  system: { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', label: 'System' },
  user: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', label: 'User' },
  assistant: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', label: 'Assistant' },
  tool: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', label: 'Tool' },
  function: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', label: 'Function' },
};
const DEFAULT_ROLE_STYLE = { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-700', label: '' };

// ============================================================================
// Sub-components
// ============================================================================

/** Render tool_calls as styled blocks */
function ToolCallsBlock({ toolCalls, label }: { toolCalls: ToolCall[]; label: string }) {
  return (
    <div className="space-y-1.5">
      {toolCalls.map((tc, i) => {
        let parsedArgs: string;
        try {
          parsedArgs = JSON.stringify(JSON.parse(tc.function?.arguments || '{}'), null, 2);
        } catch {
          parsedArgs = tc.function?.arguments || '';
        }
        return (
          <div key={tc.id || i} className="rounded-lg border border-orange-200 bg-orange-50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-semibold uppercase text-orange-700">{label}</span>
              <span className="text-[10px] font-mono text-orange-600 bg-white/60 px-1.5 py-0.5 rounded">
                {tc.function?.name || '?'}
              </span>
            </div>
            <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-words mt-1 max-h-[150px] overflow-y-auto">
              {parsedArgs}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

/** Render reasoning_content as a collapsible block */
function ReasoningBlock({ content, compact = false, label }: { content: string; compact?: boolean; label: string }) {
  const [expanded, setExpanded] = useState(false);
  const maxH = compact ? 'max-h-[150px]' : 'max-h-[300px]';

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-[10px] font-semibold uppercase text-amber-700 hover:text-amber-900"
      >
        {label}
        <span className="text-amber-400 font-normal">{expanded ? '\u25B4' : '\u25BE'}</span>
      </button>
      {expanded && (
        <div className={`mt-1 text-xs text-gray-700 whitespace-pre-wrap break-words ${maxH} overflow-y-auto leading-relaxed`}>
          {content}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SmartContent({ data, compact = false }: { data: unknown; compact?: boolean }) {
  const [showRaw, setShowRaw] = useState(false);
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const maxH = compact ? 'max-h-[200px]' : 'max-h-[300px]';

  if (data == null) {
    return <p className="text-xs text-gray-400 italic">null</p>;
  }

  // --- OpenAI Messages Array ---
  if (isMessagesArray(data)) {
    return (
      <div className="space-y-2">
        {data.map((msg, i) => {
          const style = ROLE_STYLES[msg.role] || DEFAULT_ROLE_STYLE;
          const text = extractTextContent(msg.content);
          const hasToolCalls = msg.tool_calls && msg.tool_calls.length > 0;
          const reasoning = msg.reasoning_content;
          return (
            <div key={i} className="space-y-1.5">
              <div className={`rounded-lg border ${style.bg} p-3`}>
                <span className={`text-[10px] font-semibold uppercase ${style.text}`}>
                  {style.label || msg.role}
                </span>
                {text ? (
                  <div className={`mt-1 text-xs text-gray-800 whitespace-pre-wrap break-words ${maxH} overflow-y-auto leading-relaxed`}>
                    {text}
                  </div>
                ) : !hasToolCalls && (
                  <div className="mt-1 text-xs text-gray-400 italic">{t.emptyContent}</div>
                )}
              </div>
              {reasoning && <ReasoningBlock content={reasoning} compact={compact} label={t.reasoningLabel} />}
              {hasToolCalls && <ToolCallsBlock toolCalls={msg.tool_calls!} label={t.toolCallLabel} />}
            </div>
          );
        })}
        <RawJsonToggle data={data} showRaw={showRaw} onToggle={() => setShowRaw(!showRaw)} showLabel={t.showRawJson} hideLabel={t.hideRawJson} />
      </div>
    );
  }

  // --- OpenAI Completion Response ---
  if (isCompletionResponse(data)) {
    const choice = data.choices?.[0];
    const msg = choice?.message;
    const assistantText = extractTextContent(msg?.content);
    const model = data.model;
    const toolCalls = msg?.tool_calls;
    const hasToolCalls = toolCalls && toolCalls.length > 0;
    // reasoning_content can be at message level or in provider_specific_fields
    const reasoning = msg?.reasoning_content
      || (msg?.provider_specific_fields?.reasoning_content as string | undefined);

    const hasContent = assistantText || hasToolCalls || reasoning;

    return (
      <div className="space-y-2">
        {/* Model & finish_reason header */}
        {(model || choice?.finish_reason) && (
          <div className="flex items-center gap-2">
            {model && (
              <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
                {model}
              </span>
            )}
            {choice?.finish_reason && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                choice.finish_reason === 'stop' ? 'text-green-600 bg-green-50' :
                choice.finish_reason === 'tool_calls' ? 'text-orange-600 bg-orange-50' :
                'text-gray-600 bg-gray-50'
              }`}>
                {choice.finish_reason}
              </span>
            )}
          </div>
        )}

        {/* Assistant text content */}
        {assistantText && (
          <div className="rounded-lg border bg-green-50 border-green-200 p-3">
            <span className="text-[10px] font-semibold uppercase text-green-700">Assistant</span>
            <div className={`mt-1 text-xs text-gray-800 whitespace-pre-wrap break-words ${compact ? 'max-h-[200px]' : 'max-h-[400px]'} overflow-y-auto leading-relaxed`}>
              {assistantText}
            </div>
          </div>
        )}

        {/* Reasoning content */}
        {reasoning && <ReasoningBlock content={reasoning} compact={compact} label={t.reasoningLabel} />}

        {/* Tool calls */}
        {hasToolCalls && <ToolCallsBlock toolCalls={toolCalls!} label={t.toolCallLabel} />}

        {/* If nothing extracted, show a hint */}
        {!hasContent && (
          <p className="text-xs text-gray-400 italic">{t.emptyResponse}</p>
        )}

        <RawJsonToggle data={data} showRaw={showRaw} onToggle={() => setShowRaw(!showRaw)} showLabel={t.showRawJson} hideLabel={t.hideRawJson} />
      </div>
    );
  }

  // --- Fallback: raw JSON ---
  return <JsonBlock data={data} />;
}

function RawJsonToggle({ data, showRaw, onToggle, showLabel, hideLabel }: { data: unknown; showRaw: boolean; onToggle: () => void; showLabel: string; hideLabel: string }) {
  return (
    <div>
      <button onClick={onToggle} className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors">
        {showRaw ? `${hideLabel} \u25B4` : `${showLabel} \u25BE`}
      </button>
      {showRaw && <JsonBlock data={data} />}
    </div>
  );
}

export function JsonBlock({ data }: { data: unknown }) {
  if (data == null) {
    return <p className="text-xs text-gray-400 italic">null</p>;
  }

  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);

  return (
    <pre className="text-xs font-mono bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words max-h-[400px] overflow-y-auto text-gray-800 mt-1">
      {text}
    </pre>
  );
}
